import type { ComponentPropsWithRef } from 'react';
import { Slot, Slottable } from '@radix-ui/react-slot';
import { cn } from '@/lib/utils';
import { Icon } from '@/shared-ui/atoms/icon';
import { ChevronRightIcon } from '@/shared-ui/icons/chevron-right';
import { InsideSpaceIcon } from '@/shared-ui/icons/inside-space';

type RootProps = ComponentPropsWithRef<'a'> & {
    asChild?: boolean;
    border?: boolean;
    withInsideSpace?: boolean;
};

const Root = ({
    asChild = false,
    className,
    border = false,
    withInsideSpace = false,
    ref,
    children,
    ...props
}: RootProps) => {
    const Comp = asChild ? Slot : 'a';

    return (
        <Comp
            data-slot="link-card"
            data-has-logo={withInsideSpace ? 'true' : 'false'}
            className={cn(
                'group/link-card flex h-auto w-full items-stretch bg-background text-foreground no-underline rounded-sm overflow-hidden font-serif',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                { 'border border-snk-greige': border },
                className
            )}
            ref={ref as never}
            {...props}>
            <Slottable>{children}</Slottable>
            <span data-slot="link-card-chevron" className="mr-4 inline-flex shrink-0 items-center">
                <Icon icon={ChevronRightIcon} className="size-4" />
            </span>
        </Comp>
    );
};
Root.displayName = 'LinkCard.Root';

type ImageProps = Omit<ComponentPropsWithRef<'img'>, 'alt'> & {
    alt: string;
};

const Image = ({ className, ref, alt, ...props }: ImageProps) => (
    <div className="flex overflow-hidden">
        <img
            data-slot="link-card-image"
            ref={ref}
            alt={alt}
            className={cn(
                'aspect-square shrink-0 object-cover scale-100 transition-transform duration-300 ease-out group-hover/link-card:scale-[1.15] group-focus-visible/link-card:scale-[1.15]',
                'group-data-[has-logo=true]/link-card:h-[115px] md:group-data-[has-logo=true]/link-card:h-[115px]',
                'group-data-[has-logo=false]/link-card:h-[106px] md:group-data-[has-logo=false]/link-card:h-[124px]',
                className
            )}
            {...props}
        />
    </div>
);
Image.displayName = 'LinkCard.Image';

type ContentProps = ComponentPropsWithRef<'div'>;

const Content = ({ className, ref, children, ...props }: ContentProps) => (
    <div
        data-slot="link-card-content"
        ref={ref}
        className={cn('flex min-w-0 flex-1 flex-col justify-center px-4 py-4', className)}
        {...props}>
        <span
            data-slot="link-card-icon"
            className={cn(
                'mb-2 hidden h-4 items-center',
                'group-data-[has-logo=true]/link-card:inline-flex group-data-[has-logo=false]/link-card:hidden'
            )}>
            <Icon icon={InsideSpaceIcon} className="h-4 w-auto" />
        </span>
        <p
            data-slot="link-card-text"
            className={cn(
                'line-clamp-3 overflow-hidden text-ellipsis pb-0.5',
                'group-data-[has-logo=true]/link-card:text-[16px] group-data-[has-logo=true]/link-card:leading-[19px]',
                'group-data-[has-logo=true]/link-card:md:text-[18px] group-data-[has-logo=true]/link-card:md:leading-[19px]',
                'group-data-[has-logo=false]/link-card:text-[20px] group-data-[has-logo=false]/link-card:leading-[24px]',
                'group-data-[has-logo=false]/link-card:md:text-[24px] group-data-[has-logo=false]/link-card:md:leading-[30px]'
            )}>
            {children}
        </p>
    </div>
);
Content.displayName = 'LinkCard.Content';

const LinkCard = {
    Root,
    Image,
    Content,
} as const;

export { LinkCard };
